import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Chip,
  Divider,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Business,
  People,
  AttachMoney,
  Receipt,
  Extension,
  Edit,
  LocationOn,
  Language,
  Email,
  Phone,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Public,
  TrendingUp,
  Search,
  FileDownload,
  MoreVert,
  Visibility,
  PersonAdd,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { useState } from 'react';
import { useCounty, useCountyStats, useCountyUsers, useCountyModules, useSuspendCountyUser, useUnsuspendCountyUser } from '../../hooks';
import { colors } from '../../theme/theme';
import { User, Role } from '../../types/models';
import { useSnackbar } from '../../contexts/SnackbarContext';

export const CountyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const countyId = parseInt(id || '0');
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { county, isLoading: countyLoading } = useCounty(countyId);
  const { stats, isLoading: statsLoading } = useCountyStats(countyId);
  const { users, isLoading: usersLoading } = useCountyUsers(countyId);
  const { modules, isLoading: modulesLoading } = useCountyModules(countyId);
  const suspendUser = useSuspendCountyUser();
  const unsuspendUser = useUnsuspendCountyUser();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleViewProfile = () => {
    if (selectedUser) {
      navigate(`/superadmin/county-users/${selectedUser.id}`);
      handleMenuClose();
    }
  };

  const handleEdit = () => {
    if (selectedUser) {
      navigate(`/superadmin/county-users/${selectedUser.id}/edit`);
      handleMenuClose();
    }
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;

    try {
      await suspendUser.mutateAsync({ countyId, userId: selectedUser.id });
      showSnackbar('User suspended successfully', 'success');
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to suspend user', 'error');
    }
  };

  const handleUnsuspend = async () => {
    if (!selectedUser) return;

    try {
      await unsuspendUser.mutateAsync({ countyId, userId: selectedUser.id });
      showSnackbar('User activated successfully', 'success');
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to activate user', 'error');
    }
  };

  const handleExportUsers = () => {
    if (!users) return;

    const csv = [
      ['Name', 'Email', 'User Type', 'Roles', 'Created At'].join(','),
      ...users.map((user: User) =>
        [
          user.name,
          user.email,
          user.user_type,
          user.roles?.map((r) => r.name).join('; ') || '',
          new Date(user.created_at).toLocaleDateString(),
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${county?.name}_users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (countyLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>Loading county details...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  if (!county) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>County not found</Typography>
        </Box>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: <People sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
      change: '+12%',
    },
    {
      title: 'Total Modules',
      value: stats?.total_modules || 0,
      icon: <Extension sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
      change: '+8%',
    },
    {
      title: 'Total Revenue',
      value: `KES ${(stats?.total_revenue || 0).toLocaleString()}`,
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${colors.warning.main} 0%, ${colors.warning.dark} 100%)`,
      change: '+24%',
    },
    {
      title: 'Transactions',
      value: stats?.total_transactions || 0,
      icon: <Receipt sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${colors.error.main} 0%, ${colors.error.dark} 100%)`,
      change: '+18%',
    },
  ];

  const getContactIcon = (contactName: string) => {
    const name = contactName?.toLowerCase() || '';
    if (name.includes('email')) return <Email sx={{ fontSize: 20, color: colors.primary.main }} />;
    if (name.includes('phone') || name.includes('mobile')) return <Phone sx={{ fontSize: 20, color: colors.primary.main }} />;
    if (name.includes('facebook')) return <Facebook sx={{ fontSize: 20, color: '#1877f2' }} />;
    if (name.includes('twitter') || name.includes('x')) return <Twitter sx={{ fontSize: 20, color: '#1da1f2' }} />;
    if (name.includes('instagram')) return <Instagram sx={{ fontSize: 20, color: '#e4405f' }} />;
    if (name.includes('linkedin')) return <LinkedIn sx={{ fontSize: 20, color: '#0077b5' }} />;
    return <Public sx={{ fontSize: 20, color: colors.grey[600] }} />;
  };

  // User DataGrid columns
  const userColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: colors.primary.main, fontSize: '0.875rem' }}>
            {params.value?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'user_type',
      headerName: 'User Type',
      width: 130,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />
      ),
    },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {params.value?.map((role: Role) => (
            <Chip key={role.id} label={role.name} size="small" variant="outlined" />
          ))}
        </Stack>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? 'Active' : 'Suspended'}
          size="small"
          color={params.value === 1 ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 130,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{ color: colors.grey[600] }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Module DataGrid columns
  const moduleColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Module Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Extension sx={{ fontSize: 18, color: colors.primary.main }} />
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'moduleType',
      headerName: 'Type',
      width: 150,
      valueGetter: (value: any) => value?.name || 'N/A',
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || 'No description'}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 130,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
  ];

  const filteredUsers = users?.filter((user: User) =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title={county.name}
          subtitle={`County Code: ${county.code}`}
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Counties', path: '/superadmin/counties' },
            { label: county.name },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate(`/superadmin/counties/${county.id}/edit`)}
              sx={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${colors.primary.main}40`,
              }}
            >
              Edit County
            </Button>
          }
        />

        {/* County Header Card */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
              p: 4,
              color: 'white',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '300px',
                height: '300px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '50%',
                transform: 'translate(30%, -30%)',
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={3} position="relative" zIndex={1}>
              <Avatar
                src={county.county_details?.logo}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  border: '4px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                }}
              >
                {!county.county_details?.logo && <Business sx={{ fontSize: 60 }} />}
              </Avatar>
              <Box flex={1}>
                <Typography variant="h3" fontWeight={700} gutterBottom>
                  {county.name}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                  {county.code} • {county.county_details?.office_location || 'Location not specified'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label="Active"
                    sx={{
                      bgcolor: 'rgba(76, 175, 80, 0.2)',
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      border: '1px solid',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`Onboarded ${new Date(county.county_details?.created_at || '').toLocaleDateString()}`}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      border: '1px solid',
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {county.county_details?.website && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Language sx={{ color: colors.primary.main }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Website
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        component="a"
                        href={county.county_details.website}
                        target="_blank"
                        sx={{
                          color: colors.primary.main,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {county.county_details.website}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {county.county_details?.office_location && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <LocationOn sx={{ color: colors.primary.main }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Office Location
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {county.county_details.office_location}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {statCards.map((stat, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: stat.gradient,
                  color: 'white',
                  borderRadius: 2.5,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${stat.gradient.match(/#[0-9a-f]{6}/i)?.[0]}40`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100px',
                    height: '100px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    transform: 'translate(30%, -30%)',
                  },
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        {stat.title}
                      </Typography>
                      <Typography variant="h4" fontWeight={700}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ color: '#ffffff' }}>
                    <TrendingUp sx={{ fontSize: 16 }} />
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#ffffff' }}>
                      {stat.change} from last month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Tabs Section */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                },
              }}
            >
              <Tab label="Overview" />
              <Tab label={`Users (${users?.length || 0})`} />
              <Tab label={`Modules (${modules?.length || 0})`} />
              <Tab label="Contacts" />
            </Tabs>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {/* Overview Tab */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: '#ffffff',
                      borderRadius: 2,
                      border: `1px solid ${colors.grey[200]}`,
                      borderLeft: `4px solid ${colors.success.main}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: colors.success.dark }}>
                      County Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          County Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {county.name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          County Code
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {county.code}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Onboarding Date
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {county.county_details?.created_at
                            ? new Date(county.county_details.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: '#ffffff',
                      borderRadius: 2,
                      border: `1px solid ${colors.grey[200]}`,
                      borderLeft: `4px solid ${colors.success.main}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: colors.success.dark }}>
                      Quick Stats
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Total Administrators
                        </Typography>
                        <Chip label={stats?.total_users || 0} size="small" color="primary" />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Active Modules
                        </Typography>
                        <Chip label={stats?.total_modules || 0} size="small" color="success" />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Total Transactions
                        </Typography>
                        <Chip label={stats?.total_transactions || 0} size="small" />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Users Tab */}
            {activeTab === 1 && (
              <Box>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: colors.grey[500] }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 300 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownload />}
                      onClick={handleExportUsers}
                      disabled={!users || users.length === 0}
                    >
                      Export
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PersonAdd />}
                      sx={{
                        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                      }}
                    >
                      Add User
                    </Button>
                  </Stack>
                </Box>
                <DataGrid
                  rows={filteredUsers || []}
                  columns={userColumns}
                  loading={usersLoading}
                  autoHeight
                  disableRowSelectionOnClick
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: `${colors.primary.main}08`,
                    },
                  }}
                />
              </Box>
            )}

            {/* Modules Tab */}
            {activeTab === 2 && (
              <Box>
                <DataGrid
                  rows={modules || []}
                  columns={moduleColumns}
                  loading={modulesLoading}
                  autoHeight
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: `${colors.primary.main}08`,
                    },
                  }}
                />
              </Box>
            )}

            {/* Contacts Tab */}
            {activeTab === 3 && (
              <Box>
                {county.contacts && county.contacts.length > 0 ? (
                  <Grid container spacing={2}>
                    {county.contacts.map((contact: any, index: number) => (
                      <Grid key={index} size={{ xs: 12, md: 6 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            bgcolor: '#ffffff',
                            border: `1px solid ${colors.grey[200]}`,
                            borderLeft: `4px solid ${colors.success.main}`,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: colors.success.main,
                              borderLeftColor: colors.success.dark,
                              bgcolor: `${colors.success.main}08`,
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.12)',
                            },
                          }}
                        >
                          {getContactIcon(contact.contact?.name)}
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              {contact.contact?.name || 'Contact'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {contact.value}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box textAlign="center" py={6}>
                    <Public sx={{ fontSize: 64, color: colors.grey[300], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Contact Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact details will appear here when added
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* User Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewProfile}>
          <ListItemIcon>
            <Visibility fontSize="small" sx={{ color: colors.primary.main }} />
          </ListItemIcon>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" sx={{ color: colors.warning.main }} />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        {selectedUser?.is_active === 1 ? (
          <MenuItem onClick={handleSuspend}>
            <ListItemIcon>
              <Block fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Suspend User</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleUnsuspend}>
            <ListItemIcon>
              <CheckCircle fontSize="small" sx={{ color: colors.success.main }} />
            </ListItemIcon>
            <ListItemText>Activate User</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </AdminLayout>
  );
};
